import ko from "knockout";
import _ from "lodash";
import config from "config";
import toastService from "services/toastService";
import localeService from "services/localeService";
import navHelper from "services/navHelper";
import loggingService from "services/loggingService";
import ViewModel from "viewModels/ViewModelTyped";
import FormType from "models/composer/FormType";
import authService from "services/authService";
import embeddingService from "services/embeddingService";
import featureToggleService, {
    FeatureToggle,
} from "services/featureToggleService";
import EvaluationSubmittedEventType from "enums/EvaluationSubmittedEventType";

interface IToastNotification {
    title: string;
    message: string;
    type: string;
    publishOnUserInteraction: boolean;
    hideAfter?: number;
    tag?: any;
    body: string;
}

interface EvaluationEventMessage {
    evaluationNotificationEventType: string;
}

export default class EmbeddedQualityFormEditor extends ViewModel {
    private childIFrame!: HTMLIFrameElement | null;
    formUrl: ko.Observable<string>;

    constructor(
        private formType: string,
        private scorableId: string,
        private conversationId: string,
        private isAdminContext: boolean,
        private deleteEvaluationCallback: (
            conversationId: string,
            data: any
        ) => unknown,
        private closeSidePanelCallback: () => unknown,
        private assignmentPanel?: string,
        private qualityFormVersion?: string
    ) {
        super();

        const validFormTypes = _.keys(FormType);
        const isValidFormType = _.includes(validFormTypes, formType);
        if (!isValidFormType || !scorableId) {
            loggingService.error("bad argument(s)", {
                formType,
                scorableId,
            });
        }

        // Initialize with empty string to prevent premature iframe loading
        // The URL will be set asynchronously after getting a fresh token
        this.formUrl = ko.observable("");
        this.onMessage = this.onMessage.bind(this);

        // Initialize the form URL asynchronously
        this.initializeFormUrl();
    }

    private pathForForm() {
        // Fetch the time machine branch
        const prefix = `${
            this.qualityFormVersion ? "/" + this.qualityFormVersion : ""
        }/#`;
        const context = this.isAdminContext ? "admin" : "agent";
        switch (this.formType) {
            case FormType.survey:
                return `${prefix}/surveys/${this.scorableId}`;
            case FormType.evaluation:
                return `${prefix}/${context}/interactions/${this.conversationId}/evaluations/${this.scorableId}`;
            default:
                loggingService.error("unsupported form type", {
                    formType: this.formType,
                });
                return prefix;
        }
    }

    handleNavigation(path: string) {
        return navHelper.navigateTo(path, undefined, false);
    }

    private initializeFormUrl() {
        this.getFormUrlWithFreshToken()
            .then((formUrl) => {
                this.formUrl(formUrl);
                loggingService.debug("Form URL initialized with fresh token");
            })
            .catch((error) => {
                loggingService.error("Failed to initialize form URL:", error);
            });
    }

    /**
     * Get the form URL with conditional auth token based on feature toggle
     * When PURE-5920_disable_legacy_auth is ON: no token in query params
     * When PURE-5920_disable_legacy_auth is OFF: token in query params (legacy)
     */
    getFormUrlWithFreshToken(): Promise<string> {
        const isLegacyAuthDisabled = featureToggleService.isEnabled(
            FeatureToggle.PURE_5920_DISABLE_LEGACY_AUTH
        );

        const url = new URL(config.qualityForms.uri + this.pathForForm());
        const query = new URLSearchParams();

        // Always add these common parameters
        query.append("locale", localeService.getAppLocale() || "");
        query.append("timezone", ko.i18n.getTimezone() || "");

        const isAssigneeAndDisputesFTEnabled =
            featureToggleService.isEnabled(FeatureToggle.EVALUATION_ASSIGNEE) &&
            featureToggleService.isEnabled(FeatureToggle.EVALUATION_DISPUTES);

        if (isAssigneeAndDisputesFTEnabled && this.assignmentPanel) {
            query.append("assignmentPanel", this.assignmentPanel);
        }

        // Conditionally add auth token based on feature toggle
        if (isLegacyAuthDisabled) {
            // FT ON: No token in query params
            return Promise.resolve(`${url.href}?${query.toString()}`);
        } else {
            // FT OFF: Include token in query params (legacy behavior)
            // Get fresh token and construct legacy format that the embedded form expects
            return Promise.resolve(authService.getToken())
                .then((token) => {
                    const authToken = JSON.stringify({
                        authenticated: {
                            access_token: token,
                            token_type: "bearer",
                        },
                        secure: {
                            access_token: token,
                            token_type: "bearer",
                        },
                    });
                    query.append("authToken", authToken);
                    return `${url.href}?${query.toString()}`;
                })
                .catch((error) => {
                    loggingService.error(
                        "Failed to get fresh token for form URL:",
                        error
                    );
                    // Return URL without token as fallback
                    return `${url.href}?${query.toString()}`;
                });
        }
    }

    handleToast(toast: IToastNotification) {
        if (toast.body) {
            const toastTitle = toast.title;
            const toastOpts = {
                bodyMessage: toast.body,
                publishOnUserInteraction: toast.publishOnUserInteraction,
                delay: toast.hideAfter ? toast.hideAfter * 1000 : null,
            };
            // @ts-expect-error toastService is JS and notifyMessage has optional param useLongDelay
            toastService.notifyMessage(toastTitle, toastOpts);
        } else {
            embeddingService.sendToastNotification(toast);
        }
    }

    onMessage(message: MessageEvent) {
        if (
            !this.childIFrame ||
            message.source !== this.childIFrame.contentWindow
        ) {
            return;
        }
        const isMatchingOrigin = window.origin === message.origin;
        const isMessageOriginLocalhost =
            window.origin.indexOf("localhost") !== -1;
        const shouldHandleCrossDocumentMessage =
            isMatchingOrigin || isMessageOriginLocalhost;

        if (shouldHandleCrossDocumentMessage) {
            const { type, data } = message.data;

            switch (type) {
                case "toast":
                    return this.handleToast(data);
                case "navigation":
                    return this.handleNavigation(data);
                case "evaluation-deleted":
                    return this.deleteEvaluationCallback(
                        this.conversationId,
                        data
                    );
                case "sidepanel-closed":
                    return this.closeSidePanelCallback();
                default:
                    return loggingService.debug(
                        "received unrecognized message type",
                        {
                            type: type,
                            data: data,
                        }
                    );
            }
        }
    }

    onIFrameLoaded(
        view: EmbeddedQualityFormEditor,
        { target }: Partial<Event>
    ) {
        this.childIFrame = target as HTMLIFrameElement;
        window.addEventListener("message", this.onMessage);
    }

    onDispose() {
        window.removeEventListener("message", this.onMessage);
    }

    postMessageToIFrame(message: EvaluationEventMessage) {
        if (
            this.childIFrame &&
            this.childIFrame.contentWindow &&
            message.evaluationNotificationEventType ===
                EvaluationSubmittedEventType.AI_SCORED
        ) {
            this.childIFrame.contentWindow.postMessage(
                message,
                config.qualityForms.uri
            );
        }
    }
}
